import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { ApiError, api, errorMessage } from '../../api/client';
import { launchParams, useSession, type Session } from '../../api/session';
import type { DataDeletionBody } from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { AlertIcon } from '../../shared/icons';
import { formatDate } from '../../shared/lib/date';
import { digitGroups } from '../../shared/lib/docNumber';
import {
  Action,
  Card,
  CardList,
  ConfirmCheck,
  Gutter,
  List,
  Notice,
  Prose,
  Screen,
  Section,
  SectionTitle,
  type ListTone,
} from '../../shared/ui';
import { ConfirmDialog } from '../review/staffKit';

/**
 * Удаление своих данных.
 *
 * Экран обязателен по правилам площадки, и относиться к нему как к формальной
 * странице нельзя: человек должен ПЕРЕД нажатием понимать, что именно исчезнет
 * и что вернуть это не сможет никто. Отсюда два барьера подряд — галочка и
 * отдельное подтверждение: случайно нажать «удалить» пальцем в списке пунктов
 * меню слишком легко.
 *
 * Заявка уходит на POST /api/users/me/delete-data — маршрут живёт в модуле
 * users (users.controller.ts) вместе с остальными настройками аккаунта.
 * Удалять может и тот, у кого доступ сняли: сессия его не узнаёт, поэтому
 * номер страницы сервер берёт из подписи параметров запуска, как у записи.
 * Аккаунта нет вовсе — ответ account_not_found, и экран говорит, что удалять нечего.
 */

type Stage = 'idle' | 'nothing';

const REMOVED = [
  t('user.deletion.removed.characters'),
  t('user.deletion.removed.forms'),
  t('user.deletion.removed.items'),
  t('user.deletion.removed.pets'),
  t('user.deletion.removed.abilities'),
  t('user.deletion.removed.images'),
  t('user.deletion.removed.readMarks'),
  t('user.deletion.removed.access'),
];

const KEPT = [
  t('user.deletion.kept.account'),
  t('user.deletion.kept.ban'),
  t('user.deletion.kept.audit'),
  t('user.deletion.kept.decisions'),
  t('user.deletion.kept.mentions'),
  t('user.deletion.kept.staff'),
];

// Перезапуск сессии размонтирует экран, а «только что» по сессии не отличить от
// «когда-то». У отозванного в ней нет и самой отметки: сессия его не узнаёт.
let deletedThisRun = false;

interface Props extends NavIdProps {
  /** Перезапуск сессии: роль и право записи приложение считает один раз на старте. */
  onDeleted: () => void;
}

export function DataDeletionPanel({ id, onDeleted }: Props) {
  const back = useBack();
  const session = useSession();
  const vkId = digitGroups(session.vkUserId);
  // Вернувшийся в проект в том же запуске снова может удалять.
  const deleted = deletedThisRun && session.user?.canWrite !== true;
  const deletedBefore = deleted ? null : (session.user?.deletedAt ?? null);

  const [agreed, setAgreed] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('idle');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body: DataDeletionBody = { launchParams: launchParams() };
      await api.post('/api/users/me/delete-data', body);
      deletedThisRun = true;
      onDeleted();
    } catch (cause: unknown) {
      const code = cause instanceof ApiError ? cause.code : null;
      if (code === 'account_not_found') {
        setAsking(false);
        setStage('nothing');
      } else {
        setError(errorMessage(cause));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen id={id} title={t('user.deletion.title')} back={back}>
      <Section>
        <SectionTitle>{t('user.deletion.section.stored')}</SectionTitle>
        <Gutter>
          <Prose>{t('user.deletion.stored', { id: vkId })}</Prose>
        </Gutter>
      </Section>

      {session.user === null && !deleted && stage === 'idle' ? (
        <Section>
          <Gutter>
            <Notice tone="info" title={t('user.deletion.guest.title')}>
              {t('user.deletion.guest.text')}
            </Notice>
          </Gutter>
        </Section>
      ) : null}

      <Section>
        <SectionTitle accent="pink">{t('user.deletion.section.removed')}</SectionTitle>
        <CardList>
          <Lines items={REMOVED} tone="pink" />
        </CardList>
      </Section>

      <Section>
        <SectionTitle>{t('user.deletion.section.kept')}</SectionTitle>
        <CardList>
          <Lines items={KEPT} tone="cyan" />
        </CardList>
      </Section>

      {deleted ? (
        <Section>
          <Gutter>
            <Notice tone="success" title={t('user.deletion.done.title')}>
              {doneText(session)}
            </Notice>
          </Gutter>
        </Section>
      ) : null}

      {deletedBefore === null ? null : (
        <Section>
          <Gutter>
            <Notice tone="info" title={t('user.deletion.already.title')}>
              {t('user.deletion.already.text', { date: formatDate(deletedBefore) })}
            </Notice>
          </Gutter>
        </Section>
      )}

      {stage === 'nothing' ? (
        <Section>
          <Gutter>
            <Notice tone="info" title={t('user.deletion.nothing.title')}>
              {t('user.deletion.nothing.text')}
            </Notice>
          </Gutter>
        </Section>
      ) : null}

      {!deleted && deletedBefore === null && stage === 'idle' ? (
        <Section>
          <SectionTitle accent="pink">{t('user.deletion.section.confirm')}</SectionTitle>

          {/* В рамке и со значком: это не примечание, а остановка перед
              необратимым. Поля по бокам даёт коробка, шов до согласия —
              раздел: своей распоркой тут набегало двадцать восемь. */}
          <Gutter>
            <Notice
              tone="error"
              framed
              icon={<AlertIcon tone="pink" />}
              title={t('user.deletion.warning.title')}
            >
              {t('user.deletion.warning.text')}
            </Notice>
          </Gutter>

          <Gutter>
            <ConfirmCheck checked={agreed} onChange={setAgreed}>
              {t('user.deletion.agree')}
            </ConfirmCheck>
          </Gutter>

          <Gutter>
            <Action
              tone="action"
              size="l"
              stretched
              disabled={!agreed}
              onClick={() => {
                setError(null);
                setAsking(true);
              }}
            >
              {t('user.deletion.action')}
            </Action>
          </Gutter>
        </Section>
      ) : null}

      {asking ? (
        <ConfirmDialog
          title={t('user.deletion.confirm.title')}
          text={t('user.deletion.confirm.text')}
          confirmText={t('user.deletion.confirm.action')}
          destructive
          busy={busy}
          error={error}
          onCancel={() => setAsking(false)}
          onConfirm={() => void submit()}
        />
      ) : null}
    </Screen>
  );
}

/** Отозванного перезапущенная сессия не узнаёт: user === null после удаления значит бан. */
function doneText(session: Session): string {
  if (session.user === null) return t('user.deletion.done.revoked');
  return session.selfRegistration ? t('user.deletion.done.open') : t('user.deletion.done.closed');
}

/**
 * Перечень одной карточкой. Не приглушаем: человек читает его перед
 * необратимым действием.
 *
 * Контур и маркеры красит РОЛЬ перечня: малиновый — то, что исчезнет,
 * бирюзовый — то, что останется. Два одинаковых блока подряд читались как
 * один длинный список, и разницу приходилось искать в заголовке.
 */
function Lines({ items, tone }: { items: string[]; tone: ListTone }) {
  return (
    <Card tone={tone === 'pink' ? 'rejected' : 'published'}>
      <List items={items} tone={tone} />
    </Card>
  );
}
